import { create } from 'zustand';
import { AuthState, User } from '../types/store';
import { authService } from '../service/AuthService';
import { useAvatarStore } from './avatarStore';

interface AuthStore extends AuthState {
  // Actions
  signOut: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
  initialize: () => (() => void) | void;
  handleGoogleSignIn: () => Promise<void>;
  
  // Internal actions
  _setAuthState: (state: Partial<AuthState>) => void;
}

// Request deduplication for auth status checks
let authStatusCheckPromise: Promise<void> | null = null;

export const useAuthStore = create<AuthStore>((set, get) => ({
  // Initial state
  isAuthenticated: false,
  isLoading: true,
  user: null,

  // Actions
  signOut: async () => {
    try {
      await authService.signOut();
      set({ 
        isAuthenticated: false, 
        user: null, 
        isLoading: false
      });
      // COMMENTED OUT FOR TESTING - Clear avatar when signing out
      // useAvatarStore.getState().clearAvatar();
    } catch (error) {
      console.error('Sign out error:', error);
      // Even if signOut fails, clear local state and avatar
      set({ 
        isAuthenticated: false, 
        user: null, 
        isLoading: false
      });
      // COMMENTED OUT FOR TESTING - useAvatarStore.getState().clearAvatar();
    }
  },

  checkAuthStatus: async () => {
    // Request deduplication - if there's already a check in progress, return that promise
    if (authStatusCheckPromise) {
      console.log('🔍 [AuthStore] Auth status check already in progress, reusing existing promise');
      return authStatusCheckPromise;
    }

    // Create new promise and cache it
    authStatusCheckPromise = (async () => {
      set({ isLoading: true });
      try {
        const user = await authService.getCurrentUser();
        
        if (user) {
          console.log('✅ [AuthStore] User authenticated:', user);
          set({ 
            isAuthenticated: true, 
            user, 
            isLoading: false 
          });
        } else {
          console.log('🔍 [AuthStore] No authenticated user found');
          set({ 
            isAuthenticated: false, 
            user: null, 
            isLoading: false 
          });
        }
      } catch (error) {
        console.error('❌ [AuthStore] checkAuthStatus error:', error);
        set({ 
          isAuthenticated: false, 
          user: null, 
          isLoading: false 
        });
      } finally {
        // Clear the cached promise after completion
        authStatusCheckPromise = null;
      }
    })();

    return authStatusCheckPromise;
  },

  handleGoogleSignIn: async () => {
    try {
      console.log('🔍 [AuthStore] Starting Google sign-in');
      set({ isLoading: true });
      
      await authService.signInWithGoogle();
      
      console.log('✅ [AuthStore] Google sign-in initiated - account linking handled by Lambda');
      // Auth state will be updated by the auth listener after successful sign-in
      
    } catch (error: any) {
      console.error('❌ [AuthStore] Google sign-in error:', error);
      set({ 
        isLoading: false
      });
    }
  },

  // Internal actions
  _setAuthState: (state: Partial<AuthState>) => {
    set(state);
  },

  initialize: () => {
    let eventProcessingCount = 0;
    
    // Set up auth event listener
    const cleanup = authService.setupAuthListener((event: string, data?: any) => {
      eventProcessingCount++;
      const currentState = get();
      
      console.log(`🔍 [AuthStore] Processing auth event #${eventProcessingCount}:`, event);
      console.log('🔍 [AuthStore] Current state before processing:', {
        isAuthenticated: currentState.isAuthenticated,
        hasUser: !!currentState.user,
        isLoading: currentState.isLoading
      });
      
      if (event === 'signedOut') {
        console.log('🚨 [AuthStore] SIGNEDOUT EVENT - clearing auth state (AVATAR CLEARING DISABLED FOR TESTING)');
        
        set({ 
          isAuthenticated: false, 
          user: null, 
          isLoading: false
        });
        
        // COMMENTED OUT FOR TESTING - Centralized avatar clearing - prevents multiple components from clearing simultaneously
        // try {
        //   useAvatarStore.getState().clearAvatar();
        //   console.log('✅ [AuthStore] Avatar cleared successfully');
        // } catch (error) {
        //   console.error('❌ [AuthStore] Error clearing avatar:', error);
        // }
        
        console.log('🔍 [AuthStore] SignedOut processing complete (no avatar clearing)');
        
      } else if (event === 'signedIn' || event === 'signInWithRedirect') {
        console.log('🔍 [AuthStore] User signed in, getting user info');
        // Get user info directly without setTimeout
        authService.getCurrentUser().then(user => {
          if (user) {
            console.log('✅ [AuthStore] User authenticated after sign in:', user);
            set({ 
              isAuthenticated: true, 
              user, 
              isLoading: false 
            });
          }
        }).catch(error => {
          console.error('❌ [AuthStore] Error getting user after sign in:', error);
          set({ 
            isAuthenticated: false, 
            user: null, 
            isLoading: false 
          });
        });
      }
      // Removed tokenRefresh handler - not needed and was causing infinite loops
      
      console.log(`🔍 [AuthStore] Event #${eventProcessingCount} processing complete`);
    });
    
    console.log('🔍 [AuthStore] Initializing auth store...');
    
    // Do initial auth check
    get().checkAuthStatus();

    // Return cleanup function
    return cleanup;
  },
}));
